import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { IRole } from '../../../../types/settingsDataModule';
import { SettingsService } from '../../../../services/settings/settings.service';
import { failureReason } from '../../../../../utils/http-errors';
import {
  describeRole,
  formatRoleName,
  pickableRoles,
} from '../../../../pages/organization/settings/settings-roles';

export type IUserFormDataKeys = 'firstName' | 'lastName' | 'emailAddress' | 'primaryContactNumber' | 'roles';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** Invite someone to the organization: they get an email to set a password. */
@Component({
  selector: 'app-add-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputFieldComponent,
    NgxMatInputTelComponent,
    ReactiveFormsModule,
    CustomizableButtonComponent,
  ],
  templateUrl: './add-user-form.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css'],
})
export class AddUserFormComponent implements OnInit, OnChanges, OnDestroy {
  constructor(private builder: FormBuilder) {}
  settingsService = inject(SettingsService);
  private destroyRef = inject(DestroyRef);

  @Output() onClose = new EventEmitter();
  @Output() onSuccess = new EventEmitter<void>();
  @Input({ required: true }) openModal = false;

  loading: boolean = false;

  // A failed create stays in the modal until the next try.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;
  private feedbackTimeoutId?: ReturnType<typeof setTimeout>;

  // Role selection
  showSelectUserRoleModal: boolean = false;
  selectedRoles: IRole[] = [];
  submitted: boolean = false;
  private allRoles: IRole[] = [];
  /** Roles offered in the list, most senior first. */
  roleOptions: IRole[] = [];

  userForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    primaryContactNumber: ['', Validators.required],
    roles: [[] as string[], Validators.required],
  });

  ngOnInit() {
    // The users page loads the IAM roles; this only listens.
    this.settingsService.rolesData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: IRole[]) => {
      this.allRoles = roles || [];
      this.roleOptions = pickableRoles(this.allRoles);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Each opening starts from a clean form.
    if (changes['openModal']?.currentValue === true && !changes['openModal'].firstChange) {
      this.resetForm();
    }
  }

  openSelectUserRoleModal() {
    this.showSelectUserRoleModal = true;
  }

  closeSelectUserRoleModal() {
    this.showSelectUserRoleModal = false;
  }

  getFormInput(name: IUserFormDataKeys) {
    return this.userForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter a full email address, like name@company.com.';
    } else if (email?.errors?.['required']) {
      return 'Enter the email address they will sign in with.';
    } else {
      return '';
    }
  }

  describe(role: IRole): string {
    return describeRole(role.name, this.allRoles);
  }

  label(role: IRole): string {
    return formatRoleName(role.name);
  }

  isSelected(role: IRole): boolean {
    return this.selectedRoles.some((r) => r.name === role.name);
  }

  toggleRole(role: IRole, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const next = checked
      ? [...this.selectedRoles.filter((r) => r.name !== role.name), role]
      : this.selectedRoles.filter((r) => r.name !== role.name);
    this.updateSelectedRole(next);
  }

  updateSelectedRole(roles: any) {
    this.selectedRoles = roles;
    const roleNames = this.selectedRoles.map((role) => role.name);
    this.userForm.patchValue({
      roles: roleNames,
    });
  }

  removeRole(roleId: string) {
    this.selectedRoles = this.selectedRoles.filter((role) => role.id !== roleId);
    // Role names, as the create request expects (not ids).
    this.userForm.patchValue({
      roles: this.selectedRoles.map((role) => role.name),
    });
  }

  handleSubmit() {
    this.submitted = true;
    if (this.userForm.valid) {
      this.loading = true;
      this.showFeedback = false;
      this.settingsService
        .createUser({
          firstName: this.userForm.value.firstName || '',
          lastName: this.userForm.value.lastName || '',
          emailAddress: this.userForm.value.emailAddress || '',
          phoneNumber: this.userForm.value.primaryContactNumber || '',
          authorities: this.userForm.value.roles || [],
        })
        .subscribe({
          next: () => {
            this.loading = false;
            this.onSuccess.emit();
            this.closeModal();
            this.resetForm();
          },
          error: (error) => {
            this.loading = false;
            const errorMessage =
              error?.status === 409
                ? 'Someone with this email address is already a member. Use a different address.'
                : `They were not added. ${failureReason(
                    error,
                    'Your organization wasn’t found. Black Konnect support can check your account.'
                  )}`;
            this.displayFeedback(errorMessage, 'error');
          },
          complete: () => {
            this.loading = false;
          },
        });
    }
  }

  private resetForm() {
    this.submitted = false;
    this.showFeedback = false;
    this.selectedRoles = [];
    this.userForm.reset({
      firstName: '',
      lastName: '',
      emailAddress: '',
      primaryContactNumber: '',
      roles: [],
    });
  }

  displayFeedback(message: string, type: FeedbackType) {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    this.feedbackTimeoutId = setTimeout(() => {
      this.showFeedback = false;
      this.feedbackTimeoutId = undefined;
    }, 10000);
  }

  closeModal() {
    this.onClose.emit();
  }

  ngOnDestroy() {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
  }
}
